import type { IsoDateTime } from '@omnipresentgroup/design-system';

import type { Address, Country } from '../common';

export enum ContractorState {
  ONBOARDING = 'ONBOARDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ContractState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum OnboardingStepState {
  INIT = 'INIT',
  OPEN = 'OPEN',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  DONE = 'DONE',
}

export enum InvoicingFrequencyEnum {
  PER_HOUR = 'PER_HOUR',
  PER_DAY = 'PER_DAY',
  PER_WEEK = 'PER_WEEK',
  PER_MONTH = 'PER_MONTH',
  PER_DELIVERABLE = 'PER_DELIVERABLE',
}

export enum InvoicingCurrencyEnum {
  GBP = 'GBP',
  EUR = 'EUR',
  USD = 'USD',
}

export enum BillingCurrencyEnum {
  GBP = 'GBP',
  EUR = 'EUR',
  USD = 'USD',
}

export enum EntityType {
  INDIVIDUAL = 'INDIVIDUAL',
  SOLE_TRADER = 'SOLE_TRADER',
  BUSINESS = 'BUSINESS',
  ORGANISATION = 'ORGANISATION',
  PARTNERSHIP = 'PARTNERSHIP',
}

export enum LiabilityShieldState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  AWAITING_ACTIVATION = 'AWAITING_ACTIVATION',
}

export type LiabilityShield = {
  state: LiabilityShieldState;
  startDate: string;
  endDate?: string;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
};

export type Contract = {
  id: string;
  contractorId: string;
  clientId: string;
  jobRole: string;
  invoicingCurrency: InvoicingCurrencyEnum;
  billingCurrency: BillingCurrencyEnum;
  expectedInvoicingFrequency?: InvoicingFrequencyEnum;
  rate?: string;
  startDate?: string;
  endDate?: string;
  state: ContractState;
  liabilityShieldCoverages?: LiabilityShield[];
};

export type Contractor = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  residenceCountry: Country;
  state: ContractorState;
  contracts: Contract[];
  userId: string;
  onboardingStates: {
    kycState: OnboardingStepState;
    personalState: OnboardingStepState;
    bankingAccountState: OnboardingStepState;
  };
};

export type ContractorPersonalDetails = {
  state: OnboardingStepState;
  stateLastErrorMessage?: string;
  firstName?: string;
  lastName?: string;
  residenceCountry?: Country;
  nationality?: Country;
  dateOfBirth?: string;
};

export enum BankTypesEnum {
  IBAN = 'IBAN',
  GB = 'UK Account',
  US = 'US Account',
  CA = 'CA Account',
  AU = 'AU Account',
  OTHER = 'Other',
}

export type ContractorBankDetails = {
  state: OnboardingStepState;
  stateLastErrorMessage?: string;
  bankDetails: {
    type: BankTypesEnum;
    accountHolderName: string;
    iban?: string;
    swiftBic?: string;
    aba?: string;
    institutionNumber?: string;
    branchCode?: string;
    bankName?: string;
    sortCode?: string;
    bankAccountCountry?: string;
    accountNumber?: string;
    bankStateBranch?: string;
    id?: string;
  };
  accountHolderAddress: Address;
};

export type ContractorIdentityVerification = {
  state: OnboardingStepState;
  stateLastErrorMessage?: string;
  entityType: EntityType;
  termsAgreed?: boolean;
  termsAgreedAt?: string;
  entityTypeConfirmed?: boolean;
  entityTypeConfirmedAt?: string;
  registeredLegalName?: string;
  entityEmail?: string;
  companyNumber?: string;
  headquartersAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postalCode: string;
    region?: string;
    country: Country;
  };
  legalRepresentative?: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    nationality: Country;
    countryOfResidence: Country;
  };
};

export enum OutstandingFeeTypeEnum {
  MANAGEMENT = 'MANAGEMENT',
  LIABILITY_SHIELD = 'LIABILITY_SHIELD',
}

export enum OutstandingFeeStatusEnum {
  OPEN = 'OPEN',
  AWAITING_FUNDS = 'AWAITING_FUNDS',
  PAID = 'PAID',
}

export type ContractOutstandingFee = {
  id: string;
  contractId: string;
  type: OutstandingFeeTypeEnum;
  amount: string;
  vatRate: string;
  currency: BillingCurrencyEnum;
  month: string;
  status: OutstandingFeeStatusEnum;
};
